import React, { useState, useEffect } from "react";
import { getSiteConfig, updateSiteConfig } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MediaPicker } from "@/components/MediaManager";
import { Save, Image, Loader2, CheckCircle, Plus, Trash2 } from "lucide-react";

function ImageField({ label, value, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="flex gap-2">
        <Input value={value || ""} onChange={e => onChange(e.target.value)} placeholder="URL de l'image" className="rounded-xl flex-1" />
        <Button type="button" variant="outline" onClick={() => setPickerOpen(true)} className="rounded-xl px-3">
          <Image className="w-4 h-4" />
        </Button>
      </div>
      {value && <img src={value} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => onChange(url)} />
    </div>
  );
}

function SectionEditor({ sectionKey, title, children: renderForm }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSiteConfig(sectionKey)
      .then(r => setData(r.data?.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sectionKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSiteConfig(sectionKey, data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1670B9]" /></div>;

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Quicksand'] text-lg font-semibold text-slate-800">{title}</h3>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl" data-testid={`save-${sectionKey}`}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {saved ? "Sauvegardé !" : "Enregistrer"}
        </Button>
      </div>
      {renderForm(data, setData)}
    </div>
  );
}

// =============== HERO EDITOR ===============
export function HeroEditor() {
  return (
    <SectionEditor sectionKey="hero" title="Section Hero">
      {(data, setData) => (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Titre principal</label>
            <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" data-testid="hero-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sous-titre</label>
            <Textarea value={data.subtitle || ""} onChange={e => setData({...data, subtitle: e.target.value})} rows={3} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bouton 1 - Texte</label>
              <Input value={data.btn1_text || ""} onChange={e => setData({...data, btn1_text: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bouton 1 - Lien</label>
              <Input value={data.btn1_link || ""} onChange={e => setData({...data, btn1_link: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bouton 2 - Texte</label>
              <Input value={data.btn2_text || ""} onChange={e => setData({...data, btn2_text: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bouton 2 - Lien</label>
              <Input value={data.btn2_link || ""} onChange={e => setData({...data, btn2_link: e.target.value})} className="rounded-xl" />
            </div>
          </div>
          <ImageField label="Logo" value={data.logo_url} onChange={v => setData({...data, logo_url: v})} />
          <ImageField label="Image de fond" value={data.bg_image} onChange={v => setData({...data, bg_image: v})} />
        </div>
      )}
    </SectionEditor>
  );
}

// =============== ABOUT EDITOR ===============
export function AboutEditor() {
  return (
    <SectionEditor sectionKey="about" title="Section À propos">
      {(data, setData) => (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Surtitre</label>
            <Input value={data.overline || ""} onChange={e => setData({...data, overline: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Titre</label>
            <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Paragraphe 1</label>
            <Textarea value={data.paragraph1 || ""} onChange={e => setData({...data, paragraph1: e.target.value})} rows={4} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Paragraphe 2</label>
            <Textarea value={data.paragraph2 || ""} onChange={e => setData({...data, paragraph2: e.target.value})} rows={4} className="rounded-xl" />
          </div>
          <ImageField label="Image" value={data.image_url} onChange={v => setData({...data, image_url: v})} />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Affiliation</label>
            <Input value={data.affiliation || ""} onChange={e => setData({...data, affiliation: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Caractéristiques</label>
            {(data.features || []).map((f, i) => (
              <div key={`feat-${i}-${f.title || i}`} className="flex gap-2 mb-2 items-start">
                <Input value={f.title} onChange={e => {
                  const features = [...(data.features || [])];
                  features[i] = {...features[i], title: e.target.value};
                  setData({...data, features});
                }} placeholder="Titre" className="rounded-xl w-40" />
                <Textarea value={f.desc} onChange={e => {
                  const features = [...(data.features || [])];
                  features[i] = {...features[i], desc: e.target.value};
                  setData({...data, features});
                }} placeholder="Description" rows={2} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => {
                  const features = (data.features || []).filter((_, j) => j !== i);
                  setData({...data, features});
                }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => {
              setData({...data, features: [...(data.features || []), {icon: "Dna", title: "", desc: ""}]});
            }} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        </div>
      )}
    </SectionEditor>
  );
}

// =============== RESEARCH EDITOR ===============
export function ResearchEditor() {
  return (
    <SectionEditor sectionKey="research" title="Section Recherche">
      {(data, setData) => (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Surtitre</label>
            <Input value={data.overline || ""} onChange={e => setData({...data, overline: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Titre</label>
            <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sous-titre</label>
            <Textarea value={data.subtitle || ""} onChange={e => setData({...data, subtitle: e.target.value})} rows={2} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Axes de recherche</label>
            {(data.axes || []).map((ax, i) => (
              <div key={`axis-${i}-${ax.title || i}`} className="border border-slate-100 rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Axe {i+1}</span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setData({...data, axes: (data.axes || []).filter((_, j) => j !== i)});
                  }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
                <div className="space-y-2">
                  <Input value={ax.title} onChange={e => {
                    const axes = [...(data.axes || [])]; axes[i] = {...axes[i], title: e.target.value}; setData({...data, axes});
                  }} placeholder="Titre" className="rounded-xl" />
                  <Textarea value={ax.desc} onChange={e => {
                    const axes = [...(data.axes || [])]; axes[i] = {...axes[i], desc: e.target.value}; setData({...data, axes});
                  }} placeholder="Description" rows={2} className="rounded-xl" />
                  <Input value={ax.color || "#1670B9"} onChange={e => {
                    const axes = [...(data.axes || [])]; axes[i] = {...axes[i], color: e.target.value}; setData({...data, axes});
                  }} placeholder="Couleur (#hex)" className="rounded-xl" />
                  <Input value={ax.image_url || ""} onChange={e => {
                    const axes = [...(data.axes || [])]; axes[i] = {...axes[i], image_url: e.target.value}; setData({...data, axes});
                  }} placeholder="URL image" className="rounded-xl" />
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => {
              setData({...data, axes: [...(data.axes || []), {icon: "Dna", title: "", desc: "", color: "#1670B9", image_url: ""}]});
            }} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter un axe</Button>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Objectifs</label>
            {(data.objectives || []).map((obj, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={obj} onChange={e => {
                  const objectives = [...(data.objectives || [])]; objectives[i] = e.target.value; setData({...data, objectives});
                }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => {
                  setData({...data, objectives: (data.objectives || []).filter((_, j) => j !== i)});
                }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => {
              setData({...data, objectives: [...(data.objectives || []), ""]});
            }} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        </div>
      )}
    </SectionEditor>
  );
}

// =============== FOOTER EDITOR ===============
export function FooterEditor() {
  return (
    <SectionEditor sectionKey="footer" title="Pied de page">
      {(data, setData) => (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <Textarea value={data.description || ""} onChange={e => setData({...data, description: e.target.value})} rows={3} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone 1</label>
              <Input value={data.phone1 || ""} onChange={e => setData({...data, phone1: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone 2</label>
              <Input value={data.phone2 || ""} onChange={e => setData({...data, phone2: e.target.value})} className="rounded-xl" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input value={data.email || ""} onChange={e => setData({...data, email: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Copyright</label>
            <Input value={data.copyright || ""} onChange={e => setData({...data, copyright: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Liens rapides</label>
            {(data.quick_links || []).map((link, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={link.label} onChange={e => {
                  const links = [...(data.quick_links || [])]; links[i] = {...links[i], label: e.target.value}; setData({...data, quick_links: links});
                }} placeholder="Label" className="rounded-xl w-32" />
                <Input value={link.href} onChange={e => {
                  const links = [...(data.quick_links || [])]; links[i] = {...links[i], href: e.target.value}; setData({...data, quick_links: links});
                }} placeholder="Lien" className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => {
                  setData({...data, quick_links: (data.quick_links || []).filter((_, j) => j !== i)});
                }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => {
              setData({...data, quick_links: [...(data.quick_links || []), {label: "", href: ""}]});
            }} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Réseaux sociaux</label>
            {(data.social_links || []).map((s, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <select value={s.platform} onChange={e => {
                  const links = [...(data.social_links || [])]; links[i] = {...links[i], platform: e.target.value}; setData({...data, social_links: links});
                }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm w-32">
                  {["facebook","twitter","linkedin","youtube","instagram"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input value={s.url} onChange={e => {
                  const links = [...(data.social_links || [])]; links[i] = {...links[i], url: e.target.value}; setData({...data, social_links: links});
                }} placeholder="URL" className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => {
                  setData({...data, social_links: (data.social_links || []).filter((_, j) => j !== i)});
                }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionEditor>
  );
}

// =============== NAVBAR EDITOR ===============
export function NavbarEditor() {
  return (
    <SectionEditor sectionKey="navbar" title="Barre de navigation">
      {(data, setData) => (
        <div className="space-y-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Liens de navigation</label>
          {(data.links || []).sort((a,b) => (a.order||0)-(b.order||0)).map((link, i) => (
            <div key={`nav-${i}-${link.label || i}`} className="flex gap-2 items-center mb-2">
              <Input value={link.order ?? i} onChange={e => {
                const links = [...(data.links || [])]; links[i] = {...links[i], order: parseInt(e.target.value)||0}; setData({...data, links});
              }} className="rounded-xl w-16 text-center" type="number" />
              <Input value={link.label} onChange={e => {
                const links = [...(data.links || [])]; links[i] = {...links[i], label: e.target.value}; setData({...data, links});
              }} placeholder="Label" className="rounded-xl w-32" />
              <Input value={link.href} onChange={e => {
                const links = [...(data.links || [])]; links[i] = {...links[i], href: e.target.value}; setData({...data, links});
              }} placeholder="Lien" className="rounded-xl flex-1" />
              <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                <input type="checkbox" checked={link.visible !== false} onChange={e => {
                  const links = [...(data.links || [])]; links[i] = {...links[i], visible: e.target.checked}; setData({...data, links});
                }} className="rounded" />
                Visible
              </label>
              <Button variant="ghost" size="sm" onClick={() => {
                setData({...data, links: (data.links || []).filter((_, j) => j !== i)});
              }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            setData({...data, links: [...(data.links || []), {label: "", href: "", visible: true, order: (data.links?.length || 0)}]});
          }} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter un lien</Button>
        </div>
      )}
    </SectionEditor>
  );
}

// =============== CONTACT EDITOR ===============
export function ContactEditor() {
  return (
    <SectionEditor sectionKey="contact" title="Section Contact">
      {(data, setData) => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Surtitre</label>
              <Input value={data.overline || ""} onChange={e => setData({...data, overline: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Titre</label>
              <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone 1</label>
              <Input value={data.phone1 || ""} onChange={e => setData({...data, phone1: e.target.value})} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone 2</label>
              <Input value={data.phone2 || ""} onChange={e => setData({...data, phone2: e.target.value})} className="rounded-xl" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input value={data.email || ""} onChange={e => setData({...data, email: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse ligne 1</label>
            <Input value={data.address_line1 || ""} onChange={e => setData({...data, address_line1: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse ligne 2</label>
            <Input value={data.address_line2 || ""} onChange={e => setData({...data, address_line2: e.target.value})} className="rounded-xl" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse ligne 3</label>
            <Input value={data.address_line3 || ""} onChange={e => setData({...data, address_line3: e.target.value})} className="rounded-xl" />
          </div>
        </div>
      )}
    </SectionEditor>
  );
}


// =============== HOMEPAGE SECTIONS MANAGER ===============
export function HomepageSectionsEditor() {
  return (
    <SectionEditor sectionKey="homepage_sections" title="Ordre et visibilité des sections">
      {(data, setData) => {
        const sections = (data.sections || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        const moveUp = (i) => {
          if (i === 0) return;
          const s = [...sections];
          [s[i - 1].order, s[i].order] = [s[i].order, s[i - 1].order];
          setData({ ...data, sections: s });
        };
        const moveDown = (i) => {
          if (i >= sections.length - 1) return;
          const s = [...sections];
          [s[i].order, s[i + 1].order] = [s[i + 1].order, s[i].order];
          setData({ ...data, sections: s });
        };
        const toggle = (i) => {
          const s = [...sections];
          s[i] = { ...s[i], visible: !s[i].visible };
          setData({ ...data, sections: s });
        };
        return (
          <div className="space-y-2">
            <p className="text-sm text-slate-500 mb-4">Réorganisez les sections de la page d'accueil. Activez ou désactivez chaque section.</p>
            {sections.map((s, i) => (
              <div key={s.key} className={`flex items-center gap-3 p-3 rounded-xl border ${s.visible ? "bg-white border-slate-100" : "bg-slate-50 border-slate-100 opacity-60"}`}>
                <span className="text-sm font-mono text-slate-400 w-6 text-center">{i + 1}</span>
                <div className="flex-1">
                  <span className="text-sm font-medium text-slate-800">{s.label || s.key}</span>
                  <span className="text-xs text-slate-400 ml-2">({s.key})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => moveUp(i)} disabled={i === 0} className="px-2 py-1 rounded-md text-xs bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30">&#9650;</button>
                  <button onClick={() => moveDown(i)} disabled={i >= sections.length - 1} className="px-2 py-1 rounded-md text-xs bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30">&#9660;</button>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={s.visible !== false} onChange={() => toggle(i)} className="rounded" />
                    <span className="text-xs text-slate-500">{s.visible !== false ? "Visible" : "Masqué"}</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        );
      }}
    </SectionEditor>
  );
}

// =============== SEO EDITOR ===============
export function SeoEditor() {
  return (
    <SectionEditor sectionKey="seo" title="SEO & Métadonnées">
      {(data, setData) => (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Titre du site (meta title)</label>
            <Input value={data.site_title || ""} onChange={e => setData({...data, site_title: e.target.value})} className="rounded-xl" placeholder="UR-EBioReZ | Recherche en Élevage Biologique" />
            <p className="text-xs text-slate-400 mt-1">{(data.site_title || "").length}/60 caractères recommandés</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description (meta description)</label>
            <Textarea value={data.site_description || ""} onChange={e => setData({...data, site_description: e.target.value})} rows={3} className="rounded-xl" placeholder="Description du site pour les moteurs de recherche..." />
            <p className="text-xs text-slate-400 mt-1">{(data.site_description || "").length}/160 caractères recommandés</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mots-clés (keywords)</label>
            <Input value={data.keywords || ""} onChange={e => setData({...data, keywords: e.target.value})} className="rounded-xl" placeholder="recherche, élevage, génétique, Bénin..." />
          </div>
          <ImageField label="Image Open Graph (partage réseaux sociaux)" value={data.og_image} onChange={v => setData({...data, og_image: v})} />
        </div>
      )}
    </SectionEditor>
  );
}

// =============== DIRECTOR EDITOR ===============
export function DirectorEditor() {
  return (
    <SectionEditor sectionKey="directeur" title="Profil du Responsable">
      {(data, setData) => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Nom complet</label>
              <Input value={data.name || ""} onChange={e => setData({...data, name: e.target.value})} className="rounded-xl" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Titre académique</label>
              <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" /></div>
          </div>
          <ImageField label="Photo" value={data.photo_url} onChange={v => setData({...data, photo_url: v})} />
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Biographie</label>
            <Textarea value={data.bio || ""} onChange={e => setData({...data, bio: e.target.value})} rows={4} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Parcours académique (HTML)</label>
            <Textarea value={data.academic_path || ""} onChange={e => setData({...data, academic_path: e.target.value})} rows={4} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input value={data.email || ""} onChange={e => setData({...data, email: e.target.value})} className="rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">ResearchGate URL</label>
              <Input value={data.researchgate || ""} onChange={e => setData({...data, researchgate: e.target.value})} className="rounded-xl" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Google Scholar URL</label>
              <Input value={data.google_scholar || ""} onChange={e => setData({...data, google_scholar: e.target.value})} className="rounded-xl" /></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Domaines d'expertise</label>
            {(data.expertise || []).map((e, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={e} onChange={ev => { const arr = [...(data.expertise||[])]; arr[i] = ev.target.value; setData({...data, expertise: arr}); }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setData({...data, expertise: (data.expertise||[]).filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setData({...data, expertise: [...(data.expertise||[]), ""]})} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Responsabilités</label>
            {(data.responsibilities || []).map((r, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={r} onChange={ev => { const arr = [...(data.responsibilities||[])]; arr[i] = ev.target.value; setData({...data, responsibilities: arr}); }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setData({...data, responsibilities: (data.responsibilities||[]).filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setData({...data, responsibilities: [...(data.responsibilities||[]), ""]})} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Distinctions</label>
            {(data.distinctions || []).map((d, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={d} onChange={ev => { const arr = [...(data.distinctions||[])]; arr[i] = ev.target.value; setData({...data, distinctions: arr}); }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setData({...data, distinctions: (data.distinctions||[]).filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setData({...data, distinctions: [...(data.distinctions||[]), ""]})} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        </div>
      )}
    </SectionEditor>
  );
}

// =============== ENHANCED ABOUT EDITOR ===============
export function AboutFullEditor() {
  return (
    <SectionEditor sectionKey="about" title="Page À propos complète">
      {(data, setData) => (
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Titre</label>
            <Input value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Historique</label>
            <Textarea value={data.history || ""} onChange={e => setData({...data, history: e.target.value})} rows={3} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Mission</label>
            <Textarea value={data.mission || ""} onChange={e => setData({...data, mission: e.target.value})} rows={3} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Objectif général</label>
            <Textarea value={data.general_objective || ""} onChange={e => setData({...data, general_objective: e.target.value})} rows={2} className="rounded-xl" /></div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Formation</label>
            <Textarea value={data.formation || ""} onChange={e => setData({...data, formation: e.target.value})} rows={2} className="rounded-xl" /></div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Objectifs spécifiques</label>
            {(data.specific_objectives || []).map((o, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={o} onChange={e => { const arr = [...(data.specific_objectives||[])]; arr[i] = e.target.value; setData({...data, specific_objectives: arr}); }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setData({...data, specific_objectives: (data.specific_objectives||[]).filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setData({...data, specific_objectives: [...(data.specific_objectives||[]), ""]})} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Domaines d'intervention</label>
            {(data.domains || []).map((d, i) => (
              <div key={`item-${i}`} className="flex gap-2 mb-2">
                <Input value={d} onChange={e => { const arr = [...(data.domains||[])]; arr[i] = e.target.value; setData({...data, domains: arr}); }} className="rounded-xl flex-1" />
                <Button variant="ghost" size="sm" onClick={() => setData({...data, domains: (data.domains||[]).filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setData({...data, domains: [...(data.domains||[]), ""]})} className="rounded-xl"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <ImageField label="Image principale" value={data.image_url} onChange={v => setData({...data, image_url: v})} />
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Affiliation</label>
            <Input value={data.affiliation || ""} onChange={e => setData({...data, affiliation: e.target.value})} className="rounded-xl" /></div>
        </div>
      )}
    </SectionEditor>
  );
}